import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Link as RouterLink } from 'react-router-dom';
import { DateRangePicker } from '@mui/x-date-pickers-pro/DateRangePicker';
import { SingleInputDateRangeField } from '@mui/x-date-pickers-pro/SingleInputDateRangeField';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs, { Dayjs } from 'dayjs';

import GeneralDialog from './general-dialog';
import SharedSvg from './shared-svg';
import { handleErrors } from '../utils/errors';
import { startReport } from '../api/users-api';
import { useStore } from '../components/state/zustand-store';
import { pagesConfig } from './pages-config';
import { isAbleToSeeUIElement, ToolsRoles } from '../utils/roles/client-roles';
import { DateRange } from '@mui/x-date-pickers-pro';
import { useUserSettings } from '../utils/use-user-settings';
import ChangeLogReason from './change-log-reason';
import { Messages } from '../utils/message-constants';
import { useAnalytics } from './analytics/create-events';

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Card from '@mui/material/Card';
import Stack from '@mui/material/Stack';
import Link from '@mui/material/Link';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import Divider from '@mui/material/Divider';
import { useTheme } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import InfoOutlined from '@mui/icons-material/InfoOutlined';

export interface QuickReportConfig {
  displayName: string;
  description: string;
  reportType: string;
  role: string;
  requiresDateRange?: boolean;
  requiresDaysBack?: boolean;
  requiresStartDate?: boolean;
  sourceTypeOverride?:
    | 'All_Users'
    | 'All_Groups'
    | 'User'
    | 'Group'
    | 'Resource_Calendar'
    | 'OrgUnit'
    | 'Domain';
}

interface QuickReportsDialogProps {
  open: boolean;
  handleClose: () => void;
  sourceData: string[];
  sourceType:
    | 'All_Users'
    | 'All_Groups'
    | 'User'
    | 'Group'
    | 'Resource_Calendar'
    | 'OrgUnit'
    | 'Domain';
  dialogTitle: string;
  contentDescription: string;
  reports: QuickReportConfig[];
  changeLogRoles?: string[];
  analyticsEvent?: string;
}

const EMPTY_ARRAY: string[] = [];

export default function QuickReportsDialog({
  open,
  handleClose,
  sourceData,
  sourceType,
  dialogTitle,
  contentDescription,
  reports,
  changeLogRoles = EMPTY_ARRAY,
  analyticsEvent,
}: QuickReportsDialogProps) {
  const theme = useTheme();

  const { setSnackbar } = useStore();
  const { toolsRoles = EMPTY_ARRAY, changeLogLength } = useUserSettings();
  const { createEvent } = useAnalytics();

  const [changeLogMessage, setChangeLogMessage] = useState('');
  const [dateRange, setDateRange] = useState<DateRange<Dayjs>>(() => [
    dayjs().subtract(7, 'day'),
    dayjs(),
  ]);
  const [daysBack, setDaysBack] = useState<string>('');
  const [reportStartDate, setReportStartDate] = useState<Dayjs | null>(null);

  const availableReports = reports.filter((report) => {
    return (
      isAbleToSeeUIElement(toolsRoles, ToolsRoles.MaunalRunReport.value) ||
      isAbleToSeeUIElement(toolsRoles, report.role)
    );
  });

  const [selectedReportType, setSelectedReportType] = useState<string | null>(
    null
  );
  const selectedReportForMutation = availableReports.find(
    (r) => r.reportType === selectedReportType
  );
  let isChangeLogRequired = false;
  if (selectedReportForMutation) {
    isChangeLogRequired =
      (isAbleToSeeUIElement(changeLogRoles, ToolsRoles.MaunalRunReport.value) &&
        isAbleToSeeUIElement(toolsRoles, ToolsRoles.MaunalRunReport.value)) ||
      (isAbleToSeeUIElement(changeLogRoles, selectedReportForMutation.role) &&
        isAbleToSeeUIElement(toolsRoles, selectedReportForMutation.role));
  }

  const handleCloseAndReset = () => {
    setSelectedReportType(null);
    setChangeLogMessage('');
    setDateRange([dayjs().subtract(7, 'day'), dayjs()]);
    setDaysBack('');
    setReportStartDate(null);
    handleClose();
  };

  const requiresDateRange =
    selectedReportForMutation?.requiresDateRange || false;
  const requiresDaysBack = selectedReportForMutation?.requiresDaysBack || false;
  const requiresStartDate =
    selectedReportForMutation?.requiresStartDate || false;

  const isDateRangeValid =
    !requiresDateRange || (dateRange[0]?.isValid() && dateRange[1]?.isValid());
  const isDaysBackValid =
    !requiresDaysBack || (daysBack !== '' && Number(daysBack) > 0);
  const isStartDateValid =
    !requiresStartDate || (reportStartDate?.isValid() ?? false);
  const { mutate, isPending: isCreating } = useMutation({
    mutationFn: () => {
      const startDateTimestamp =
        requiresDateRange && dateRange[0]
          ? dateRange[0].startOf('day').valueOf()
          : requiresStartDate && reportStartDate
          ? reportStartDate.startOf('day').valueOf()
          : 0;
      const endDateTimestamp =
        requiresDateRange && dateRange[1]
          ? dateRange[1].endOf('day').valueOf()
          : 0;

      return startReport(
        {
          reportName: selectedReportForMutation
            ? selectedReportForMutation.displayName
            : 'Quick Report',
          manualRun: true,
          scheduled: false,
          reportType: selectedReportType || '',
          runImmediately: true,
          useReportName: false,
          runWithoutSaving: true,
          autoShare: false,
          dynamicReportName: false,
          allSource: false,
          startDate: startDateTimestamp,
          endDate: endDateTimestamp,
          daysback: requiresDaysBack ? Number(daysBack) : null,
          reportOnLastXDays: 0,
          regexTypeSelectedList: [],
          _notifyViaEmail: false,
          daysOfWeek: false,
          sources: sourceData,
          sourceType:
            selectedReportForMutation?.sourceTypeOverride || sourceType,
        },
        changeLogMessage
      );
    },
    onSuccess: (data) => {
      setSnackbar({
        open: true,
        severity: 'success',
        message: Messages.QuickReports.ReportOpened,
      });
      window.open(
        `https://docs.google.com/spreadsheets/d/${data.spreadsheetId}/edit#gid=0`,
        '_blank',
        'noopener, noreferrer'
      );
      handleCloseAndReset();
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage: Messages.Errors.Network.Generating('a report'),
      });
    },
    onSettled: () => {
      if (analyticsEvent) {
        createEvent(analyticsEvent);
      }
    },
  });

  const isGenerateDisabled =
    isCreating ||
    !selectedReportType ||
    availableReports.length === 0 ||
    !isDateRangeValid ||
    !isDaysBackValid ||
    !isStartDateValid ||
    (isChangeLogRequired && changeLogMessage.length < changeLogLength);

  return (
    <GeneralDialog
      open={open}
      handleClose={handleCloseAndReset}
      maxWidth="sm"
      secondaryButton={{
        title: 'CANCEL',
        action: handleCloseAndReset,
        loading: false,
        disabled: false,
        shown: true,
        alignLeft: true,
        sx: {
          color: 'text.secondary',
          borderColor: 'text.secondary',
          background: 'transparent',
        },
      }}
      primaryButton={{
        title: 'GENERATE REPORT',
        action: mutate,
        loading: isCreating,
        disabled: isGenerateDisabled,
        hasTooltip: availableReports.length === 0,
        tooltipTitle: Messages.QuickReports.NoPermission,
      }}
      dialogContent={
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            borderRadius: '4px',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
            <SharedSvg sx={{ mt: 1, mr: 2 }} />
            <Box>
              <Typography
                variant="h6"
                sx={{ color: 'text.primary', fontWeight: 600 }}
              >
                Quick Reports
              </Typography>
              <Typography
                variant="caption"
                sx={{ color: 'text.secondary', display: 'block', mt: 0.5 }}
              >
                {dialogTitle}
              </Typography>
            </Box>
          </Box>

          <Divider
            sx={{
              width: '600px',
              borderTop: '1px solid',
              borderColor: 'divider',
              mx: -3,
            }}
          />

          <Box
            sx={{
              width: '100%',
              maxHeight: (() => {
                if (
                  requiresDateRange &&
                  isChangeLogRequired &&
                  selectedReportType
                ) {
                  return '250px'; // Both sections visible
                }
                if (
                  (requiresDateRange || isChangeLogRequired) &&
                  selectedReportType
                ) {
                  return '350px'; // One section visible
                }
                return '500px'; // No sections visible
              })(),
              overflowY: 'auto',
              overflowX: 'hidden',
              px: 0,
              transition: 'max-height 0.3s ease-in-out',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                padding: '16px 24px 8px 24px',
                flexDirection: 'column',
                alignItems: 'flex-start',
                gap: '16px',
                alignSelf: 'stretch',
                mx: -3,
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  width: '100%',
                }}
              >
                <Typography variant="body2" sx={{ color: 'text.primary' }}>
                  {contentDescription}
                </Typography>
                <Tooltip title={Messages.QuickReports.OneTimeRunInfo} arrow>
                  <IconButton
                    size="small"
                    sx={{ color: theme.palette.info.main }}
                  >
                    <InfoOutlined fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>

              <Stack spacing={1} sx={{ width: '100%' }}>
                {reports.map((report) => {
                  const hasPermission =
                    isAbleToSeeUIElement(
                      toolsRoles,
                      ToolsRoles.MaunalRunReport.value
                    ) || isAbleToSeeUIElement(toolsRoles, report.role);
                  const isSelected = selectedReportType === report.reportType;

                  const cardContent = (
                    <Card
                      variant="outlined"
                      sx={{
                        display: 'flex',
                        padding: '8px 8px 8px 16px',
                        alignItems: 'flex-start',
                        gap: '16px',
                        alignSelf: 'stretch',
                        borderColor: isSelected ? 'primary.main' : 'divider',
                        bgcolor: isSelected
                          ? theme.palette.mode === 'dark'
                            ? theme.palette.primary.dark
                            : theme.palette.primary.light
                          : hasPermission
                          ? 'transparent'
                          : theme.palette.action.disabledBackground,
                        cursor: hasPermission ? 'pointer' : 'not-allowed',
                        opacity: hasPermission ? 1 : 0.6,
                        borderRadius: 1,
                        transition: 'all 0.2s',
                        '&:hover': hasPermission
                          ? { borderColor: 'primary.main' }
                          : {},
                      }}
                      onClick={() => {
                        if (hasPermission) {
                          setSelectedReportType(report.reportType);
                        }
                      }}
                    >
                      <Box>
                        <Typography
                          variant="body1"
                          sx={{
                            fontWeight: 400,
                            color: 'text.primary',
                            mb: 0.5,
                          }}
                        >
                          {report.displayName}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ color: 'text.secondary' }}
                        >
                          {report.description}
                        </Typography>
                      </Box>
                    </Card>
                  );

                  if (!hasPermission) {
                    return (
                      <Tooltip
                        key={report.reportType}
                        title={Messages.QuickReports.NoPermissionSingle}
                        arrow
                        placement="top"
                      >
                        <span>{cardContent}</span>
                      </Tooltip>
                    );
                  }

                  return <Box key={report.reportType}>{cardContent}</Box>;
                })}
              </Stack>
            </Box>
          </Box>

          {requiresDateRange && selectedReportType && (
            <Stack
              sx={{
                display: 'flex',
                padding: '8px',
                flexDirection: 'column',
                alignItems: 'flex-start',
                alignSelf: 'stretch',
                borderRadius: '4px',
                bgcolor:
                  theme.palette.mode === 'dark'
                    ? theme.palette.grey[800]
                    : theme.palette.grey[50],
                border: '1px solid',
                borderColor: 'divider',
                mt: 1,
                width: '100%',
                boxSizing: 'border-box',
              }}
            >
              <Typography
                variant="body2"
                sx={{ color: 'text.primary', fontWeight: 600 }}
              >
                Configuration
              </Typography>
              <Box sx={{ width: '100%' }}>
                <Typography
                  variant="body2"
                  sx={{
                    color: 'text.primary',
                    fontWeight: 400,
                    mb: 0.5,
                    ml: 2.0,
                  }}
                >
                  Date Range
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: 'text.secondary',
                    fontStyle: 'italic',
                    display: 'block',
                    mb: 1,
                    ml: 2.0,
                  }}
                >
                  {Messages.QuickReports.DateRangeHint}
                </Typography>
                <DateRangePicker
                  value={dateRange}
                  onChange={(newValue) => {
                    if (!newValue) return;
                    setDateRange(newValue);
                  }}
                  slots={{ field: SingleInputDateRangeField }}
                  sx={{ width: 357.5, ml: 2.0 }}
                />
                <Typography
                  sx={{
                    fontWeight: 400,
                    fontSize: '12px',
                    lineHeight: '20px',
                    letterSpacing: '0.4px',
                    verticalAlign: 'middle',
                    color: 'text.secondary',
                    mt: 0.5,
                    ml: 4.0,
                  }}
                >
                  Exclusive of Selected Dates
                </Typography>
              </Box>
            </Stack>
          )}

          {requiresDaysBack && selectedReportType && (
            <Stack
              sx={{
                display: 'flex',
                padding: '8px',
                flexDirection: 'column',
                alignItems: 'flex-start',
                alignSelf: 'stretch',
                borderRadius: '4px',
                bgcolor:
                  theme.palette.mode === 'dark'
                    ? theme.palette.grey[800]
                    : theme.palette.grey[50],
                border: '1px solid',
                borderColor: 'divider',
                mt: 1,
                width: '100%',
                boxSizing: 'border-box',
              }}
            >
              <Typography
                variant="body2"
                sx={{ color: 'text.primary', fontWeight: 600 }}
              >
                Configuration
              </Typography>
              <Box sx={{ width: '100%' }}>
                <Typography
                  variant="body2"
                  sx={{
                    color: 'text.primary',
                    fontWeight: 400,
                    mb: 0.5,
                    ml: 2.0,
                  }}
                >
                  Days Back
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: 'text.secondary',
                    fontStyle: 'italic',
                    display: 'block',
                    mb: 1,
                    ml: 2.0,
                  }}
                >
                  {Messages.QuickReports.DaysBackHint}
                </Typography>
                <TextField
                  label="Days Back *"
                  type="number"
                  value={daysBack}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (val === '' || Number(val) >= 0) setDaysBack(val);
                  }}
                  size="small"
                  sx={{ width: 357.5, ml: 2.0 }}
                  slotProps={{ htmlInput: { min: 1 } }}
                />
              </Box>
            </Stack>
          )}

          {requiresStartDate && selectedReportType && (
            <Stack
              sx={{
                display: 'flex',
                padding: '8px',
                flexDirection: 'column',
                alignItems: 'flex-start',
                alignSelf: 'stretch',
                borderRadius: '4px',
                bgcolor:
                  theme.palette.mode === 'dark'
                    ? theme.palette.grey[800]
                    : theme.palette.grey[50],
                border: '1px solid',
                borderColor: 'divider',
                mt: 1,
                width: '100%',
                boxSizing: 'border-box',
              }}
            >
              <Typography
                variant="body2"
                sx={{ color: 'text.primary', fontWeight: 600 }}
              >
                Configuration
              </Typography>
              <Box sx={{ width: '100%' }}>
                <Typography
                  variant="body2"
                  sx={{
                    color: 'text.primary',
                    fontWeight: 400,
                    mb: 0.5,
                    ml: 2.0,
                  }}
                >
                  Report Start Date
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: 'text.secondary',
                    fontStyle: 'italic',
                    display: 'block',
                    mb: 1,
                    ml: 2.0,
                  }}
                >
                  Provide a start date for your report's data.
                </Typography>
                <DatePicker
                  label="Report Start Date *"
                  value={reportStartDate}
                  onChange={(newValue) => setReportStartDate(newValue)}
                  slotProps={{
                    textField: { size: 'small', sx: { ml: 2.0, width: 357.5 } },
                  }}
                />
              </Box>
            </Stack>
          )}

          {isChangeLogRequired && selectedReportType && (
            <ChangeLogReason
              changeLogMessage={changeLogMessage}
              setChangeLogMessage={setChangeLogMessage}
              changeLogLength={changeLogLength}
            />
          )}

          <Box
            sx={{
              width: '100%',
              borderColor: 'divider',
              pt: 2,
            }}
          >
            <Typography
              variant="body2"
              sx={{ display: 'block', color: 'text.secondary' }}
            >
              Looking for more control? Go to{' '}
              <Link
                component={RouterLink}
                to={`/${pagesConfig.reports.link}`}
                variant="body2"
                sx={{
                  color: 'primary.main',
                  fontWeight: 700,
                  textDecoration: 'underline',
                }}
                onClick={handleCloseAndReset}
              >
                Reports
              </Link>{' '}
              to configure a report with full parameters.
            </Typography>
          </Box>
        </Box>
      }
    />
  );
}
